import { LanguageSwitcher } from '@/components/language-switcher'
import { LoadingIndicator } from '@/components/loading-indicator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState(t('auth.loggingIn'))
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password)
      // Change message before navigating
      setLoadingMessage(t('auth.loadingDashboard'))
      // Minimum 1s delay to avoid flickering
      setTimeout(() => {
        navigate({ to: '/' })
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.invalidCredentials'))
      setIsLoading(false)
    }
  }

  // Loading state - centered animation
  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <LoadingIndicator message={loadingMessage} />
      </div>
    )
  }

  return (
    <div className="flex min-h-svh overflow-hidden bg-background">
      {/* Left side - Form */}
      <div className="flex flex-1 flex-col justify-center overflow-y-auto px-6 py-8 sm:px-16 lg:px-24">
        <div className="mx-auto w-full max-w-sm">
          {/* Language switcher */}
          <div className="mb-2">
            <LanguageSwitcher variant="bare" />
          </div>

          {/* Logo */}
          <div className="mb-8">
            <h1 className="font-serif text-2xl font-bold text-foreground">{t('app.name')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.welcome')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                className="h-11 text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                {t('auth.password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                required
                className="h-11 text-base"
              />
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="h-11 w-full">
              {t('auth.signIn')}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-muted-foreground pb-safe">
            {t('auth.protectedBy')}
          </p>
        </div>
      </div>

      {/* Right side - Visual (hidden on mobile) */}
      <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-center lg:overflow-y-auto lg:bg-accent">
        <div className="max-w-md px-8">
          <div className="rounded-2xl bg-card p-8 shadow-sm">
            <blockquote className="text-lg font-medium text-foreground">
              "{t('auth.testimonial')}"
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10" />
              <div>
                <p className="text-sm font-medium text-foreground">{t('auth.testimonialAuthor')}</p>
                <p className="text-xs text-muted-foreground">{t('auth.testimonialRole')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
